import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import type { ReactNode } from 'react'
import { View } from 'react-native'

/**
 * One row in a profile group: the glyph on a pink disc, a title, a line beneath in soft ink,
 * and a chevron (or whatever control the row carries). Rows in a group are divided by a
 * hairline; the group is the white card.
 */
export const ProfileRow = ({
	glyph,
	title,
	line,
	onPress,
	trailing,
	last = false,
}: {
	glyph: GlyphName
	title: string
	line?: string
	onPress?: () => void
	trailing?: ReactNode
	last?: boolean
}) => (
	<Pressable
		onPress={onPress}
		disabled={!onPress}
		accessibilityRole={onPress ? 'button' : undefined}
		accessible={!!onPress}
		accessibilityLabel={line ? `${title}. ${line}` : title}
		className="min-h-[68px] flex-row items-center py-3"
		style={{ gap: 14 }}
	>
		{!last && (
			<View
				className="absolute right-0 bottom-0 h-px"
				style={{ left: 48, backgroundColor: colors.divider }}
			/>
		)}
		<View className="h-10 w-10 items-center justify-center rounded-full bg-white">
			<Glyph name={glyph} size={20} color={colors.ink} />
		</View>
		<VStack gap={2} className="flex-1">
			<Text size="body0" weight="bold" numberOfLines={1}>
				{title}
			</Text>
			{!!line && (
				<Text size="body1" tone="soft" numberOfLines={1}>
					{line}
				</Text>
			)}
		</VStack>
		{trailing ?? (onPress ? <Glyph name="chevron" size={14} color={colors.ink} /> : null)}
	</Pressable>
)
